import { InfoNotice } from '@/components/patterns/info-notice'
import { LoadingState } from '@/components/patterns/loading-state'
import { CashDrawerCloseWorkflow } from '@/components/pos/CashDrawerCloseWorkflow'
import { CashDrawerOpenWorkflow } from '@/components/pos/CashDrawerOpenWorkflow'
import { CashDrawerReceiptLines } from '@/components/pos/CashDrawerReceipt'
import { CashDrawerExpectedDisclosure } from '@/components/pos/CashDrawerReconciliation'
import { PrintCashDrawerSlipButton } from '@/components/pos/PrintCashDrawerSlip'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { formatClosedDrawerDate } from '@/lib/services/cash-drawers/cash-drawers.balancing'
import { useCashDrawerSessionNavigation } from '@/lib/services/cash-drawers/cash-drawers.query'
import { Link, Navigate } from '@tanstack/react-router'
import { ArrowRight, ChevronLeft, ChevronRight, PlusCircle } from 'lucide-react'

/**
 * @param {{
 *   isRefreshing: boolean,
 *   canViewHistory: boolean,
 *   onAdjust: () => void,
 *   onRefresh: () => Promise<void>,
 *   requestedSessionId?: number,
 *   openingMode: boolean,
 *   onSelectSession: (sessionId: number | undefined, view?: 'open', replace?: boolean) => void,
 *   orgId: string,
 *   session: import('@/lib/services/cash-drawers/cash-drawers.types').CashDrawerSession | null,
 *   timezone: string,
 *   workstation: import('@/lib/services/cash-drawers/cash-drawers.types').CashDrawerWorkstation,
 * }} props
 */
export function CashDrawerRegisterBalancing({
  isRefreshing,
  canViewHistory,
  onAdjust,
  onRefresh,
  requestedSessionId,
  openingMode,
  onSelectSession,
  orgId,
  session,
  timezone,
  workstation,
}) {
  const navigation = useCashDrawerSessionNavigation(workstation.id, requestedSessionId, {
    enabled: canViewHistory,
    organizationId: orgId,
    throwOnError: false,
  })
  const selected = openingMode || !canViewHistory ? null : navigation.data?.selected
  const shownClosedSession = selected?.closed_at ? selected : null
  const shownLiveSession =
    !openingMode &&
    session &&
    Number(selected?.id ?? requestedSessionId ?? session.id) === Number(session.id)
      ? session
      : null
  const isOlder = selected && Number(selected.id) !== Number(navigation.data?.latest_session_id)
  const path = usePathHelper()
  const latestLink = path.cashDrawerBalancing
    .index()
    .using({ orgId })
    .search({
      workstation_id: workstation.id,
      session_id: canViewHistory ? navigation.data?.latest_session_id : session?.id,
      view: undefined,
    }).link
  const selectedLink = path.cashDrawerBalancing
    .index()
    .using({ orgId })
    .search({
      workstation_id: workstation.id,
      session_id: selected?.id ?? shownLiveSession?.id,
      view: undefined,
    }).link
  const selectionUnavailable =
    !openingMode &&
    requestedSessionId &&
    !shownLiveSession &&
    (!canViewHistory || navigation.isError)
  const waitingForSelection =
    !openingMode && !shownLiveSession && canViewHistory && navigation.isPending

  return (
    <div className="space-y-3">
      {!openingMode && !requestedSessionId && (selected?.id || shownLiveSession?.id) ? (
        <Navigate {...selectedLink} replace />
      ) : null}
      <BalancingRegisterBar
        canGoBack={!openingMode && Boolean(navigation.data?.previous_session_id)}
        canGoForward={!openingMode && Boolean(navigation.data?.next_session_id)}
        canViewHistory={canViewHistory}
        historyLoading={navigation.isFetching}
        latestLink={isOlder || openingMode || selectionUnavailable ? latestLink : undefined}
        onAdjust={shownLiveSession ? onAdjust : undefined}
        onOpenTill={
          shownClosedSession && !session && !isRefreshing
            ? () => onSelectSession(undefined, 'open')
            : undefined
        }
        onShowNextDay={() => onSelectSession(navigation.data.next_session_id)}
        onShowPreviousDay={() => onSelectSession(navigation.data.previous_session_id)}
        session={shownLiveSession}
        status={
          selected
            ? selected.closed_at
              ? 'Closed'
              : 'Open'
            : shownLiveSession
              ? 'Open'
              : requestedSessionId && !openingMode
                ? null
                : 'Closed'
        }
        selectedOpenedAt={selected?.opened_at}
        shownSession={shownClosedSession}
        timezone={timezone}
      />

      {canViewHistory && navigation.isError ? (
        <InfoNotice
          title={
            requestedSessionId ? 'This session is unavailable' : 'Earlier counts are unavailable'
          }
          tone="danger"
          action={
            <Button type="button" variant="notice" size="xs" onClick={() => navigation.refetch()}>
              Try again
            </Button>
          }
        >
          {requestedSessionId
            ? 'Choose Latest session or try again.'
            : 'You can still manage the current register.'}
        </InfoNotice>
      ) : null}

      {selectionUnavailable ? (
        !canViewHistory && isRefreshing ? (
          <LoadingState label="Refreshing register…" surface="card" />
        ) : !canViewHistory ? (
          <InfoNotice title="Session history is unavailable">
            You don’t have permission to view earlier sessions.
          </InfoNotice>
        ) : null
      ) : waitingForSelection ? (
        <LoadingState label="Loading session…" surface="card" />
      ) : shownClosedSession ? (
        <PreviousDrawerDay session={shownClosedSession} />
      ) : shownLiveSession ? (
        <CashDrawerCloseWorkflow
          key={shownLiveSession.id}
          session={shownLiveSession}
          onSuccess={() => {
            if (!canViewHistory && requestedSessionId) onSelectSession(undefined, undefined, true)
          }}
        />
      ) : selected && !openingMode ? (
        <InfoNotice
          title="Refresh the session details"
          action={
            <Button type="button" variant="notice" size="xs" onClick={onRefresh}>
              Refresh register
            </Button>
          }
        >
          This session changed on another device. Refresh before recording activity.
        </InfoNotice>
      ) : (
        <CashDrawerOpenWorkflow
          workstationId={workstation.id}
          onSuccess={(openedSession) => onSelectSession(openedSession.id, undefined, true)}
          onRefresh={async () => {
            await onRefresh()
            if (openingMode) onSelectSession(undefined, undefined, true)
          }}
        />
      )}
    </div>
  )
}

function BalancingRegisterBar({
  canGoBack = false,
  canGoForward = false,
  canViewHistory = false,
  historyLoading = false,
  latestLink,
  onAdjust,
  onOpenTill,
  onShowNextDay,
  onShowPreviousDay,
  session,
  status,
  selectedOpenedAt,
  shownSession,
  timezone,
}) {
  const openedAt = session?.opening.recorded_at ?? selectedOpenedAt
  const selectedDate = shownSession?.closed_at ?? (status === 'Open' ? openedAt : null)
  const previousDisabled = !canGoBack || historyLoading
  const nextDisabled = !canGoForward || historyLoading
  const previousButton = (
    <Button
      type="button"
      variant="outline"
      size="icon-xs"
      aria-label="Previous session"
      disabled={previousDisabled}
      className="disabled:pointer-events-none"
      onClick={onShowPreviousDay}
    >
      <ChevronLeft aria-hidden="true" className="size-4" />
    </Button>
  )
  const nextButton = (
    <Button
      type="button"
      variant="outline"
      size="icon-xs"
      aria-label="Next session"
      disabled={nextDisabled}
      className="disabled:pointer-events-none"
      onClick={onShowNextDay}
    >
      <ChevronRight aria-hidden="true" className="size-4" />
    </Button>
  )

  return (
    <Card size="flush" surface="subtle">
      <CardContent className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 @sm:px-5">
        {/* The status wraps under the date before the actions give up their spot on the right. */}
        <div className="flex min-w-0 flex-[1_1_20rem] items-center gap-3">
          {canViewHistory ? (
            <div className="flex shrink-0 items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  {previousDisabled ? (
                    // Disabled buttons cannot take focus; the wrapper keeps the reason reachable.
                    <span className="inline-flex" tabIndex={0} aria-label="Previous session">
                      {previousButton}
                    </span>
                  ) : (
                    previousButton
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {historyLoading
                    ? 'Loading session history'
                    : canGoBack
                      ? 'View the previous session for this register'
                      : 'No earlier session for this register'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  {nextDisabled ? (
                    <span className="inline-flex" tabIndex={0} aria-label="Next session">
                      {nextButton}
                    </span>
                  ) : (
                    nextButton
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {historyLoading
                    ? 'Loading session history'
                    : canGoForward
                      ? 'View the next session for this register'
                      : 'No later session for this register'}
                </TooltipContent>
              </Tooltip>
            </div>
          ) : null}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <Typography variant="sectionTitle">
              {selectedDate
                ? formatClosedDrawerDate(selectedDate, timezone)
                : status === 'Closed'
                  ? 'Cash drawer counted'
                  : 'Session details'}
            </Typography>
            {status ? (
              <Typography variant="supporting">
                {shownSession
                  ? `Counted ${dayjs(shownSession.closed_at).tz(timezone).format('h:mm A')}${shownSession.closed_by ? ` by ${shownSession.closed_by}` : ''}`
                  : status === 'Open'
                    ? `Started ${dayjs(openedAt).tz(timezone).format('h:mm A')}`
                    : 'Ready to start'}
              </Typography>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {latestLink ? (
            <Button asChild variant="link" size="inline">
              <Link {...latestLink}>
                Go to latest session
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            </Button>
          ) : null}
          {onOpenTill ? (
            <Can I="operate" a="cash-drawers">
              <Button type="button" variant="outline" size="sm" onClick={onOpenTill}>
                Start cash drawer
              </Button>
            </Can>
          ) : null}
          {onAdjust ? (
            <Button type="button" variant="outline" size="sm" onClick={onAdjust}>
              <PlusCircle aria-hidden="true" className="size-4" />
              Add or remove cash
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

function PreviousDrawerDay({ session }) {
  return (
    <>
      <Card size="flush" className="@container overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-elevated px-5 py-4 @sm:px-6">
            <div className="min-w-0">
              <Typography variant="sectionTitle" className="block">
                Count summary
              </Typography>
            </div>
          </div>

          <div className="space-y-4 px-5 py-5 @sm:px-6">
            <CashDrawerExpectedDisclosure
              lines={session.receipt?.expected ?? []}
              expectedCash={session.closing_expected_cash}
            />
            <CashDrawerReceiptLines
              aria-label="Cash drawer count"
              lines={session.receipt?.count ?? []}
            />
          </div>

          {session.closing_note ? (
            <div className="border-t px-5 py-4 @sm:px-6">
              <Typography variant="caption" className="block">
                Count note
              </Typography>
              <Typography variant="supporting" className="mt-1 block">
                {session.closing_note}
              </Typography>
            </div>
          ) : null}
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <PrintCashDrawerSlipButton sessionId={session.id} variant="default" size="default" />
      </div>
    </>
  )
}
